import { useQuery } from "@tanstack/react-query";

import { get } from "api";
import { DiscountAppliesTo, DiscountType, Models } from "api/types";
import RewardsIcon from "atoms/icon/icons/ic_rewards_ol.svg";
import { DiscountDetails } from "molecules/discount/DiscountDetails";
import { FreePackageGroupDetails } from "molecules/free-package-group/FreePackageGroupDetails";
import { getInclusionsCount } from "services/ListingHelperService";

interface RewardDetailsProps {
  inclusions?: Models<"InclusionListingsModel">;
  discount?: Models<"DiscountModel">;
  freePackageGroup?: Models<"FreePackageGroupModel">;
  meetsRewardMinSpendRequirement?: boolean;
  rewardMinimumSpendInCents?: number;
}
export const RewardDetails = (props: RewardDetailsProps) => {
  const { data: memberships, isLoading: areMembershipsLoading } = useQuery({
    queryKey: ["/memberships"],
    queryFn: () => get("/memberships"),
    staleTime: Infinity,
  });
  const { data: listings, isLoading: areListingsLoading } = useQuery({
    queryKey: ["/listings"],
    queryFn: () => get("/listings"),
    staleTime: Infinity,
  });

  const hasFreePackageGroup = !!props.freePackageGroup;

  const platformPackageAndTreatmentVariantCount =
    listings?.reduce(
      (acc, listing) => acc + (listing.packages?.length ?? 0),
      0
    ) ?? 0;

  const inclusionsCount = getInclusionsCount(props.inclusions);

  const discountDetailsTitle = getDiscountDetailTitle(
    !!memberships?.map((m) => m.active).length,
    `Apply this reward in cart to receive`,
    `Scan the QR code in-office and show to staff to claim this reward!`,
    props.discount
  );

  const freePackageGroupDetailsTitle = getFreePackageDetailTitle(
    "Redeem this reward to receive",
    props.freePackageGroup,
    inclusionsCount
  );

  if (areMembershipsLoading || areListingsLoading) return null;

  return (
    <div className="flex flex-col gap-6">
      {hasFreePackageGroup ? (
        <FreePackageGroupDetails
          {...props}
          title={freePackageGroupDetailsTitle}
          itemType={"reward"}
        />
      ) : (
        <DiscountDetails
          {...props}
          icon={RewardsIcon}
          title={discountDetailsTitle}
          itemType={"reward"}
          meetsRewardMinSpendRequirement={props.meetsRewardMinSpendRequirement}
          rewardMinimumSpendInCents={props.rewardMinimumSpendInCents}
        />
      )}
    </div>
  );
};

const getDiscountDetailTitle = (
  includeMemberships: boolean,
  baseMessage: string,
  inOfficeDiscountTitle: string,
  discount?: Models<"DiscountModel">
) => {
  const membershipText = includeMemberships
    ? ` or membership (1st month only)`
    : "";

  if (!discount) {
    return inOfficeDiscountTitle;
  }

  const isAppliesToAll = discount.appliesTo === DiscountAppliesTo.AppliesToAll;
  const isCashDiscount = discount.type === DiscountType.Cash;
  const isPercentageDiscount = discount.type === DiscountType.Percentage;
  const percentOff = (discount.percentOff * 100).toFixed(0);

  if (isAppliesToAll) {
    if (isCashDiscount) {
      return `${baseMessage} $${
        discount.amountOff / 100
      } off your next purchase in-app!`;
    }
    if (isPercentageDiscount && discount.percentOff !== 1) {
      return `${baseMessage} ${percentOff}% off any service${membershipText} in-app!`;
    }
    return `${baseMessage} 1 free service${membershipText} in-app!`;
  }

  if (isPercentageDiscount && discount.percentOff !== 1) {
    return `${baseMessage} ${percentOff}% off of the following services:`;
  }

  if (isCashDiscount) {
    return `${baseMessage} $${
      discount.amountOff / 100
    } off when you purchase at least one of the following services in-app:`;
  }

  return `${baseMessage} 1 of the following services for free:`;
};

const getFreePackageDetailTitle = (
  baseMessage: string,
  freePackageGroup?: Models<"FreePackageGroupModel">,
  inclusionsCount?: number
) => {
  if (!freePackageGroup || !inclusionsCount) return "";
  const isChooseFrom = freePackageGroup.hasSelectableInclusions;
  const isSingleItem =
    !freePackageGroup.hasSelectableInclusions && inclusionsCount === 1;
  const isIncludeAll =
    !freePackageGroup.hasSelectableInclusions && inclusionsCount > 1;
  if (isChooseFrom)
    return `${baseMessage} ${freePackageGroup.numberOfSelections} of the following services for free:`;
  if (isSingleItem) return `${baseMessage} the following service for free:`;
  if (isIncludeAll) return `${baseMessage} the following services for free:`;
  return baseMessage;
};
